import { byteText, fileStoragePath, fill, t, type FileStorageFacts } from '@bms2/shared';
import { useApi } from './api';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { useSuperAdmin } from '../../shell/SuperAdmin';
import { whenText } from '../../shell/viewerDates';
import styles from './RecordPage.module.css';
import own from './FileStorageBlock.module.css';

/** The first twelve hex digits of a hash, which tell two copies apart without filling the line. */
function shortHash(hash: string): string {
  return hash.slice(0, 12);
}

/**
 * 🔒 Decision 7620 (T3124, I1a): the Storage block on a file's details, drawn
 * for a super admin alone and asked of the server only for one. It names the
 * stored object's key, its size as recorded and as the store holds it, sealed or
 * plain, how many files share the bytes (naming those the reader may open), and
 * each Mirror holding a copy. A key the store does not hold says so in red.
 */
export function FileStorageBlock({ fileId, apiBaseUrl = '' }: { fileId: string; apiBaseUrl?: string }) {
  const superAdmin = useSuperAdmin();
  const zone = useViewerZone();
  const format = useDateFormat();
  const read = useApi<FileStorageFacts>(superAdmin ? fileStoragePath(fileId) : null, apiBaseUrl);
  if (!superAdmin || read.state.kind !== 'ready') return null;
  const facts = read.state.data;
  return (
    <div className={styles.field} data-file-storage="true" data-file-storage-missing={facts.missing ? 'true' : 'false'} data-file-storage-sealed={facts.sealed ? 'true' : 'false'} data-file-storage-shared={facts.sharedBy.total}>
      <span className={styles.fieldLabel}>{t('file.storage.title')}</span>
      <div className={own.lines}>
      <span className={styles.hint} data-file-storage-key={facts.key}>
        {fill(t('file.storage.key'), { key: facts.key })}{facts.keyPrefix === null ? ` (${t('file.storage.beforePrefix')})` : ''}
      </span>
      <span className={styles.hint}>{fill(t('file.storage.size'), { number: facts.versionNumber, size: `${byteText(facts.sizeBytes)} (${facts.sizeBytes} B)` })}</span>
      {facts.missing
        ? <span className={own.missing} data-file-storage-missing-line="true">{t('file.storage.missing')}</span>
        : <span className={styles.hint}>{fill(t('file.storage.storedSize'), { size: facts.storedBytes === null ? '—' : `${byteText(facts.storedBytes)} (${facts.storedBytes} B)` })}</span>}
      <span className={styles.hint}>{facts.sealed ? t('file.storage.sealed') : t('file.storage.plain')}</span>
      <span className={styles.hint}>{fill(t('file.storage.hash'), { hash: facts.contentHash })}</span>
      {facts.sharedBy.total <= 1
        ? <span className={styles.hint} data-file-storage-shared-line="1">{t('file.storage.alone')}</span>
        : (
          <span className={styles.hint} data-file-storage-shared-line={facts.sharedBy.total}>
            {fill(t('file.storage.shared'), { count: facts.sharedBy.total })}
            {' '}{facts.sharedBy.files.map((file) => file.name).join(', ')}
            {facts.sharedBy.hidden > 0 ? ` ${fill(t('file.storage.hidden'), { count: facts.sharedBy.hidden })}` : ''}
          </span>
        )}
      {facts.mirrors.length === 0 ? <span className={styles.hint} data-file-storage-mirror="none">{t('file.storage.noMirror')}</span> : null}
      {facts.mirrors.map((copy) => (
        <span key={copy.targetName} className={styles.hint} data-file-storage-mirror={copy.targetName} data-file-storage-mirror-same={copy.sameHash ? 'true' : 'false'}>
          {fill(copy.sameHash ? t('file.storage.mirror') : t('file.storage.mirrorOlder'), { name: copy.targetName, hash: shortHash(copy.contentHash), when: whenText(copy.copiedAt, zone, format) })}
        </span>
      ))}
      </div>
    </div>
  );
}
